/**
 * CarTrack Pro logo. The mark + wordmark are inlined as JSX (mirrors
 * docs/brand/logo.svg) so the monochrome strokes inherit `currentColor` from
 * the parent and the accent needle keeps the brand blue.
 *
 * `variant="full"` renders the speedometer mark + "CarTrack Pro" wordmark
 * (desktop top bar). `variant="mark"` renders just the speedometer mark
 * (mobile top bar / tight spaces).
 */

const ACCENT = '#2997ff';
const MUTED = '#8a94a6';

type LogoVariant = 'full' | 'mark';

interface LogoProps {
  variant?: LogoVariant;
  /** Rendered height in px. Width scales with the source aspect ratio. */
  height?: number;
  title?: string;
}

const FULL_RATIO = 210 / 40;
const MARK_RATIO = 40 / 40;

export function Logo({ variant = 'full', height = 24, title = 'CarTrack Pro' }: LogoProps) {
  if (variant === 'mark') {
    return (
      <svg
        viewBox="0 0 40 40"
        height={height}
        width={height * MARK_RATIO}
        role="img"
        aria-label={title}
        style={{ display: 'block', color: 'currentColor' }}
      >
        <g fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
          <path d="M11 30.7 A14 14 0 1 1 29 30.7" />
          <line x1="20" y1="20" x2="27" y2="13" stroke={ACCENT} />
        </g>
        <circle cx="20" cy="20" r="2.6" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 210 40"
      height={height}
      width={height * FULL_RATIO}
      role="img"
      aria-label={title}
      style={{ display: 'block', color: 'currentColor' }}
    >
      <g fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
        <path d="M11 30.7 A14 14 0 1 1 29 30.7" />
        <line x1="20" y1="20" x2="27" y2="13" stroke={ACCENT} />
      </g>
      <circle cx="20" cy="20" r="2.6" fill="currentColor" />
      <text
        x="46"
        y="27.5"
        fontFamily="-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,system-ui,sans-serif"
        fontSize="21"
        fontWeight="600"
        letterSpacing="-0.6"
        fill="currentColor"
      >
        CarTrack
        <tspan fontWeight="400" fill={MUTED}>
          {' Pro'}
        </tspan>
      </text>
    </svg>
  );
}
